import { useRef, useState } from 'react'
import {
  getCategoriasPorSector,
  getSubCategoriasPorCategoria,
  getSegmentosPorSubCategoria,
  getSubSegmentosPorSegmento,
} from 'src/services/axiomaService'

/**
 * Feature flag: cascada categoría → subcategoría → segmento → subsegmento
 * filtrada por sector del repuesto.
 *
 * - `false` (actual): listas libres, como antes del cambio por jerarquía.
 * - `true`: vuelve a filtrar opciones por sector vía endpoints por_*.
 *
 * Cambiar solo este booleano para reactivar la cascada sin tocar pantallas.
 */
export const HABILITAR_JERARQUIA_CASCADA = false

/**
 * Hook para la cascada de jerarquía por sector del repuesto:
 * categoría → subcategoría → segmento → subsegmento.
 *
 * El backend filtra cada nivel por el sector y por el NOMBRE de sus padres
 * (/v1/categorias/por_sector, /v1/subcategorias/por_categoria,
 * /v1/segmentos/por_subcategoria, /v1/subsegmentos/por_segmento), por lo que
 * el hook resuelve los nombres a partir de los ids seleccionados.
 *
 * Si `HABILITAR_JERARQUIA_CASCADA` es false, o el material no trae `sector`,
 * el hook queda inactivo (`activa === false`) y cada pantalla usa las listas
 * completas.
 */
export const useJerarquiaCascada = () => {
  const [activa, setActiva] = useState(false)
  const [categorias, setCategorias] = useState([])
  const [subcategorias, setSubcategorias] = useState([])
  const [segmentos, setSegmentos] = useState([])
  const [subsegmentos, setSubsegmentos] = useState([])

  // Copia síncrona de las listas: los `cambiar*` encadenados (p. ej. al
  // precargar) no pueden depender del estado de React aún no re-renderizado.
  const listasRef = useRef({
    categorias: [],
    subcategorias: [],
    segmentos: [],
    subsegmentos: [],
  })

  // Contexto de la rama seleccionada (el backend filtra por nombres).
  const ctxRef = useRef({
    sector: null,
    categoriaNombre: null,
    subcategoriaNombre: null,
    segmentoNombre: null,
  })

  const asignarLista = (nivel, lista, setState) => {
    listasRef.current[nivel] = lista
    setState(lista)
  }

  const cambiarSegmento = async (segmentoId) => {
    if (!HABILITAR_JERARQUIA_CASCADA) return []
    const ctx = ctxRef.current
    asignarLista('subsegmentos', [], setSubsegmentos)
    const segmento = listasRef.current.segmentos.find(
      (s) => s.id === segmentoId,
    )
    ctx.segmentoNombre = segmento?.nombre ?? null
    if (!ctx.sector || !ctx.categoriaNombre || !ctx.subcategoriaNombre || !segmento) {
      return []
    }
    const response = await getSubSegmentosPorSegmento({
      sector: ctx.sector,
      categoria: ctx.categoriaNombre,
      subcategoria: ctx.subcategoriaNombre,
      segmento: segmento.nombre,
    })
    const lista = response?.subsegmentos ?? []
    asignarLista('subsegmentos', lista, setSubsegmentos)
    return lista
  }

  const cambiarSubcategoria = async (subcategoriaId) => {
    if (!HABILITAR_JERARQUIA_CASCADA) return []
    const ctx = ctxRef.current
    asignarLista('segmentos', [], setSegmentos)
    asignarLista('subsegmentos', [], setSubsegmentos)
    ctx.segmentoNombre = null
    const subcategoria = listasRef.current.subcategorias.find(
      (s) => s.id === subcategoriaId,
    )
    ctx.subcategoriaNombre = subcategoria?.nombre ?? null
    if (!ctx.sector || !ctx.categoriaNombre || !subcategoria) {
      return []
    }
    const response = await getSegmentosPorSubCategoria({
      sector: ctx.sector,
      categoria: ctx.categoriaNombre,
      subcategoria: subcategoria.nombre,
    })
    const lista = response?.segmentos ?? []
    asignarLista('segmentos', lista, setSegmentos)
    return lista
  }

  const cambiarCategoria = async (categoriaId) => {
    if (!HABILITAR_JERARQUIA_CASCADA) return []
    const ctx = ctxRef.current
    asignarLista('subcategorias', [], setSubcategorias)
    asignarLista('segmentos', [], setSegmentos)
    asignarLista('subsegmentos', [], setSubsegmentos)
    ctx.subcategoriaNombre = null
    ctx.segmentoNombre = null
    const categoria = listasRef.current.categorias.find(
      (c) => c.id === categoriaId,
    )
    ctx.categoriaNombre = categoria?.nombre ?? null
    if (!ctx.sector || !categoria) {
      return []
    }
    const response = await getSubCategoriasPorCategoria({
      sector: ctx.sector,
      categoria: categoria.nombre,
    })
    const lista = response?.subcategorias ?? []
    asignarLista('subcategorias', lista, setSubcategorias)
    return lista
  }

  /**
   * Carga la cascada para un material. Precarga cada nivel siguiendo los ids
   * ya guardados (revision_* con fallback a los del repuesto) y devuelve los
   * ids que siguen siendo válidos dentro de la cascada; los niveles cuyo valor
   * guardado ya no pertenece a la rama vuelven null.
   * Devuelve null si el material no trae sector (cascada inactiva).
   */
  const iniciar = async (material) => {
    const sector = material?.sector ?? null
    ctxRef.current = {
      sector,
      categoriaNombre: null,
      subcategoriaNombre: null,
      segmentoNombre: null,
    }
    asignarLista('categorias', [], setCategorias)
    asignarLista('subcategorias', [], setSubcategorias)
    asignarLista('segmentos', [], setSegmentos)
    asignarLista('subsegmentos', [], setSubsegmentos)

    // Listas libres: no llamar endpoints por_* ni filtrar por sector
    if (!HABILITAR_JERARQUIA_CASCADA || !sector) {
      setActiva(false)
      return null
    }
    setActiva(true)

    const resuelto = {
      categoriaId: null,
      subcategoriaId: null,
      segmentoId: null,
      subsegmentoId: null,
    }

    const categoriasResponse = await getCategoriasPorSector(sector)
    asignarLista(
      'categorias',
      categoriasResponse?.categorias ?? [],
      setCategorias,
    )

    const categoriaId =
      material.revision_categoria_sugerida_id ??
      material.repuesto_categoria_id ??
      material.categoria_id ??
      null
    const subcategoriasCargadas = await cambiarCategoria(categoriaId)
    if (!ctxRef.current.categoriaNombre) return resuelto
    resuelto.categoriaId = categoriaId

    if (!subcategoriasCargadas.length) return resuelto
    const subcategoriaId =
      material.revision_subcategoria_sugerida_id ??
      material.repuesto_subcategoria_id ??
      material.subcategoria_id ??
      null
    const segmentosCargados = await cambiarSubcategoria(subcategoriaId)
    if (!ctxRef.current.subcategoriaNombre) return resuelto
    resuelto.subcategoriaId = subcategoriaId

    if (!segmentosCargados.length) return resuelto
    const segmentoId =
      material.revision_segmento_sugerido_id ??
      material.repuesto_segmento_id ??
      material.segmento_id ??
      null
    const subsegmentosCargados = await cambiarSegmento(segmentoId)
    if (!ctxRef.current.segmentoNombre) return resuelto
    resuelto.segmentoId = segmentoId

    const subsegmentoId =
      material.revision_subsegmento_sugerido_id ??
      material.repuesto_subsegmento_id ??
      material.subsegmento_id ??
      null
    if (subsegmentosCargados.some((s) => s.id === subsegmentoId)) {
      resuelto.subsegmentoId = subsegmentoId
    }

    return resuelto
  }

  return {
    activa,
    categorias,
    subcategorias,
    segmentos,
    subsegmentos,
    iniciar,
    cambiarCategoria,
    cambiarSubcategoria,
    cambiarSegmento,
  }
}
